import React, {useEffect, useMemo, useState} from 'react';
import Drawer from '../../../components/Modals/DrawerComponent2';
import {useDispatch, useSelector} from 'react-redux';
import useAzureService from '../../../services/azure';
import useJiraService from '../../../services/jira';
import useIntegrationStatus from '../../../hooks/useIntegrationStatus';
import {HiChevronRight, HiInformationCircle, HiSearch, HiX} from 'react-icons/hi';
import {toggleUserSelection, setImportStep, resetImportFlow, clearUsers} from '../../../redux/slices/manageUsers';
import {User, ImportStep} from '../../../types/manageUsers';
import PersonAvatar from './PersonAvatar';
import ImportUsersSkeleton from './ImportUsersSkeleton';
import EmailCollectionStep from './EmailCollectionStep';
import SyncUsersModal from './SyncUsersModal';
import {IntegrationActiveType} from '../../Settings/types';
import { isValidEmail } from '../../../utils/csvUtils';

interface ImportUsersDrawerProps {
  isVisible: boolean;
  onClose: () => void;
}

const ImportUsersDrawer: React.FC<ImportUsersDrawerProps> = ({ isVisible, onClose }) => {
  const dispatch = useDispatch();
  const { isIntegrationActive } = useIntegrationStatus();
  const { fetchAzureUsers } = useAzureService();
  const { fetchJiraUsers } = useJiraService();

  const [activeTab, setActiveTab] = useState<'azure' | 'jira'>('azure');
  const [searchTerm, setSearchTerm] = useState('');

  // Determine which integrations are available
  const hasAzure = isIntegrationActive('azure');
  const hasJira = isIntegrationActive('jira');

  // Set initial tab based on availability
  useEffect(() => {
    if (isVisible) {
      if (hasAzure) setActiveTab('azure');
      else if (hasJira) setActiveTab('jira');
    }
  }, [isVisible, hasAzure, hasJira]);

  const {
    users,
    initialUsers,
    loading: usersLoading,
    importLoading: importUsersLoading,
    importStep,
    userEmails,
  } = useSelector((state: any) => state.manageUsers);

  // Load users when drawer opens or active tab changes
  useEffect(() => {
    const load = async () => {
      if (!isVisible) return;
      
      // Clear current users before fetching from new source
      dispatch(clearUsers());
      
      if (activeTab === 'azure' && hasAzure) {
        await fetchAzureUsers();
      } else if (activeTab === 'jira' && hasJira) {
        await fetchJiraUsers();
      }
    };
    load();
  }, [isVisible, activeTab, hasAzure, hasJira, dispatch]);

  // Clear Redux state when drawer closes
  useEffect(() => {
    if (!isVisible) {
      dispatch(resetImportFlow());
      dispatch(clearUsers());
      setSearchTerm('');
    }
  }, [isVisible, dispatch]);

  // Only show users that are not yet imported (selected === false initially)
  const allSelectableUsers: User[] = useMemo(() => {
    const initialMap = new Map(initialUsers.map((u: User) => [u.id, u.selected]));
    return users
      .filter((u: User) => !initialMap.get(u.id))
      .sort((a: User, b: User) => (a.display_name || '').toLowerCase().localeCompare((b.display_name || '').toLowerCase()));
  }, [users, initialUsers]);

  const nonImportedUsers: User[] = useMemo(() => {
    return allSelectableUsers.filter((user: User) => {
        if (!searchTerm) return true;
        const q = searchTerm.toLowerCase();
        return (
          (user.display_name || '').toLowerCase().includes(q) ||
          (user.email || '').toLowerCase().includes(q)
        );
      });
  }, [allSelectableUsers, searchTerm]);

  const selectedUsers = useMemo(() => {
    return allSelectableUsers.filter(u => u.selected);
  }, [allSelectableUsers]);

  const needsEmailCollection = useMemo(() => {
    return selectedUsers.some(user => {
      const hasOriginalEmail = !!(user.email && user.email.trim() !== '');
      const collected = (userEmails[user.id] ?? '').trim();
      const collectedValid = isValidEmail(collected);
      return !hasOriginalEmail && !collectedValid;
    });
  }, [selectedUsers, userEmails]);

  const handleToggleUser = (userId: string) => {
    dispatch(toggleUserSelection(userId));
  };

  const handleSelectAll = () => {
    const allSelected = nonImportedUsers.every((u: User) => u.selected);
    nonImportedUsers.forEach((u: User) => {
      if (allSelected && u.selected) {
        dispatch(toggleUserSelection(u.id));
      } else if (!allSelected && !u.selected) {
        dispatch(toggleUserSelection(u.id));
      }
    });
  };

  const [showConfirm, setShowConfirm] = useState(false);
  
  const handleNext = () => {
    if (needsEmailCollection) {
      dispatch(setImportStep(ImportStep.EMAIL_COLLECTION));
    } else {
      setShowConfirm(true);
    }
  };

  const handleBackToSelection = () => {
    dispatch(setImportStep(ImportStep.USER_SELECTION));
  };

  const handleProceedToImport = () => {
    if (selectedCount === 0) {
      dispatch(setImportStep(ImportStep.USER_SELECTION));
      return;
    }
    setShowConfirm(true);
  };

  const selectedCount = selectedUsers.length;

  useEffect(() => {
    if (importStep === ImportStep.EMAIL_COLLECTION && selectedCount === 0) {
      dispatch(setImportStep(ImportStep.USER_SELECTION));
    }
  }, [importStep, selectedCount, dispatch]);

  return (
    <Drawer
      isOpen={isVisible}
      onClose={onClose}
      position="right"
      size="50"
      minsize="50"
    >
      <div className="h-screen flex flex-col bg-light-surfacePrimary dark:bg-tertiary-dark">
        {/* Header */}

        <div className="border-b border-gray-500 rounded-b-2xl dark:border-gray-700 px-6 py-6 bg-secondary-light dark:bg-secondary-dark shadow-sm">
          {importStep === ImportStep.USER_SELECTION && hasAzure && hasJira && (
            <div className="pb-3 w-fit min-w-[300px]">
              <div className="flex p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <button
                  onClick={() => setActiveTab("azure")}
                  className={`px-5 flex-1 py-1.5 text-sm font-medium rounded-md transition-all duration-200 text-left ${
                    activeTab === "azure"
                      ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm"
                      : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  }`}
                >
                  Azure DevOps
                </button>
                <button
                  onClick={() => setActiveTab("jira")}
                  className={`flex-1 py-1.5 text-sm font-medium rounded-md transition-all duration-200 text-left px-5 ${
                    activeTab === "jira"
                      ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm"
                      : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  }`}
                >
                  Jira
                </button>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-light-text-primary dark:text-white">
                {importStep === ImportStep.EMAIL_COLLECTION
                  ? "Provide Email Addresses"
                  : `Import People from ${activeTab === "azure" ? "Azure DevOps" : "Jira"}`}
              </h2>
              <p className="text-sm text-light-text-secondary dark:text-gray-400 mt-1">
                {importStep === ImportStep.EMAIL_COLLECTION
                  ? "Add email addresses for people that don't have one to complete the import"
                  : `Import your existing people from ${activeTab === "azure" ? "Azure DevOps" : "Jira"}. These changes will be applied exclusively within Cadence Pro.`}
              </p>
            </div>
            {/* <button
              onClick={onClose}
              className="p-2 hover:bg-light-interactive-hover dark:hover:bg-hover-dark rounded-lg transition-all duration-200"
            >
              <HiX className="w-5 h-5 text-light-text-secondary dark:text-gray-400" />
            </button> */}
          </div>
        </div>

        {importStep === ImportStep.EMAIL_COLLECTION ? (
          <EmailCollectionStep
            selectedUsers={selectedUsers}
            onBack={handleBackToSelection}
            onProceed={handleProceedToImport}
          />
        ) : (
          <>
            {/* Search and Actions */}
            <div className="divide-y px-6 dark:divide-gray-700 divide-gray-200">
              <div className="flex justify-between items-center space-x-3 pt-6 pb-4">
                <div className="relative flex-grow max-w-md">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <HiSearch className="w-4 h-4 mr-2 text-gray-500" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search people..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-9 pr-3 py-1.5 ml-1 text-sm border border-gray-300 text-gray-900 rounded-md leading-5 bg-secondary-light
                    placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-secondary-dark
                    dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                  />
                </div>

                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleSelectAll}
                    disabled={nonImportedUsers.length === 0}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-gray-900 bg-white border border-gray-200
                    hover:bg-gray-100 focus:ring-4 focus:ring-gray-200 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed disabled:opacity-60
                    dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:text-white transition-colors duration-200"
                  >
                    {nonImportedUsers.length > 0 &&
                    nonImportedUsers.every((u: User) => u.selected)
                      ? "Deselect All"
                      : "Select All"}
                  </button>
                </div>
              </div>

              {/* Stats and Info */}
              <div className="flex flex-row pt-4 justify-between items-center">
                {usersLoading ? (
                  <>
                    <div className="flex items-center space-x-4">
                      <div className="h-5 w-64 rounded bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />
                      <div className="h-4 w-56 rounded bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {searchTerm
                          ? `${nonImportedUsers.length} available people`
                          : `${nonImportedUsers.length} people available for import`}
                      </span>
                      {selectedCount > 0 && (
                        <span className="text-sm text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 rounded-md">
                          {selectedCount} selected
                        </span>
                      )}
                    </div>

                    <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                      <HiInformationCircle className="w-4 h-4 mr-1" />
                      Only non-imported people are shown
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-auto px-6">
              {usersLoading ? (
                <ImportUsersSkeleton />
              ) : (
                <div className="h-full overflow-hidden flex flex-col pt-4 pb-3">
                  {nonImportedUsers.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                        <HiInformationCircle className="w-6 h-6 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        {searchTerm
                          ? `No people found matching "${searchTerm}"`
                          : "All People Already Imported"}
                      </h3>
                      <p className="text-base w-[80%] text-gray-500 dark:text-gray-400 mb-4">
                        {searchTerm
                          ? "Try a different search term"
                          : `All available people from ${activeTab === "azure" ? "Azure DevOps" : "Jira"} have already been imported to Cadence Pro`}
                      </p>
                    </div>
                  ) : (
                    <div
                      className={`max-h-full overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg`}
                    >
                      <div className="divide-y divide-gray-200 dark:divide-gray-700">
                        {nonImportedUsers.map((user: User) => (
                          <div
                            key={user.id}
                            className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors ${
                              user.selected
                                ? "bg-blue-50 dark:bg-blue-900/20"
                                : ""
                            }`}
                            onClick={() => handleToggleUser(user.id)}
                          >
                            <div className="flex items-center space-x-3">
                              <input
                                type="checkbox"
                                checked={user.selected}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleToggleUser(user.id);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
                              />
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <div className="w-8 h-8">
                                    <PersonAvatar
                                      className="!rounded-md"
                                      name={user.display_name}
                                    />
                                  </div>
                                  <div className="w-full">
                                    <div className="flex items-center w-full">
                                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                                        {user.display_name}
                                      </p>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      {userEmails[user.id] ||
                                        user.email ||
                                        "No email available"}
                                      {userEmails[user.id] &&
                                        userEmails[user.id] !== user.email && (
                                          <span className="ml-1 text-blue-600 dark:text-blue-400">
                                            (Updated)
                                          </span>
                                        )}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* Footer */}
        {importStep === ImportStep.USER_SELECTION && (
          <div className="p-6 border-t rounded-t-md border-gray-200 dark:border-gray-700 flex-shrink-0 rounded-br-xl rounded-bl-xl">
            <div className="flex justify-between space-x-3">
              <button
                onClick={onClose}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:ring-4 focus:ring-gray-200 dark:bg-gray-600 dark:text-gray-300 dark:border-gray-500 dark:hover:bg-gray-700 dark:focus:ring-gray-700 transition-colors duration-200"
              >
                Cancel
              </button>

              <button
                onClick={handleNext}
                disabled={importUsersLoading || !selectedCount}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 disabled:bg-blue-400 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 transition-colors duration-200"
              >
                {importUsersLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Importing...
                  </>
                ) : (
                  <>
                    {needsEmailCollection ? (
                      <>
                        Next
                        <HiChevronRight className="w-4 h-4 ml-2" />
                      </>
                    ) : (
                      `Import ${selectedCount > 0 ? `(${selectedCount})` : ""}`
                    )}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {showConfirm && (
          <SyncUsersModal
            activeTab={
              activeTab === "azure"
                ? IntegrationActiveType.AZURE
                : IntegrationActiveType.JIRA
            }
            setShowImportConfirm={setShowConfirm}
            hasChangesToImport={selectedCount > 0}
          />
        )}
      </div>
    </Drawer>
  );
};

export default ImportUsersDrawer;


